import { useState } from 'react'
import { createPortal } from 'react-dom'

import type { Reisefotos } from '../../api/types'
import { XIcon } from '../../components/Icons'
import { useEscape } from '../../hooks/useEscape'
import { useReisefotos } from '../../hooks/queries'
import { ausFeld, zeitText, zuFeld, type Versatz } from '../../lib/ortszeit'
import { Ortssuche, type Ortstreffer } from '../map/Ortssuche'
import { useTexte } from '../../texte'

/** A photo of the trip that knows where and when it was taken. */
type Reisefoto = Reisefotos['media'][number]

/**
 * "Wo und wann?" -- the question before a weather card about a chosen
 * place and hour goes out (#83, 1.13.0). From use: "Wetter an einem anderen
 * Standort / Uhrzeit: Auswahl eines Ortes (über Ortssuche) und Auswahl
 * Datum / Zeit (Zeitvorschlag: aktuelle Zeit - 12 h)."
 *
 * **Two ways to the place** (1.14.0, from the issue: "Ortswahl: 2
 * Möglichkeiten: Eingabefeld und Suchen; Ortsdaten und Zeit aus einem Foto
 * der Reise; Auswahl des Fotos, dann nochmal die Option die Daten zu
 * ändern"). The first is the place search of the map (`Ortssuche`), the
 * same one and no second: the server asks Apple Maps with the words a
 * person typed, and the sentence about that stands in the search itself.
 * The second is a photo of this trip with coordinates: place and time come
 * from its EXIF data, and the fields stay open to change afterwards. Day
 * and time are fields in the trip's clock, prefilled with twelve hours ago.
 *
 * **A chooser replaces the sheet while it is open**, instead of lying on
 * top: `.ortssuche` is a lower layer than `.rueckfrage`, and a search under
 * the sheet it was opened from would be a search nobody can reach.
 *
 * Built like `Tagwahl`: on `document.body`, Escape and a tap on the
 * background say no, the safe way has the focus.
 */
export function Wetterwahl({
  tripId,
  versatz,
  onWahl,
  onAbbruch,
}: {
  tripId: number
  versatz: Versatz
  onWahl: (wahl: { lat: number; lon: number; at: string }) => void
  onAbbruch: () => void
}) {
  const t = useTexte()
  const vorschlag = zuFeld(new Date(Date.now() - 12 * 3600_000).toISOString(), versatz)
  const [ort, setOrt] = useState<Ortstreffer | null>(null)
  const [foto, setFoto] = useState<Reisefoto | null>(null)
  const [tag, setTag] = useState(vorschlag.slice(0, 10))
  const [zeit, setZeit] = useState(vorschlag.slice(11, 16))
  const [offen, setOffen] = useState<'suche' | 'fotos' | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  useEscape(() => (offen !== null ? setOffen(null) : onAbbruch()))

  function senden() {
    if (ort === null || tag === '' || zeit === '') return
    const at = ausFeld(`${tag}T${zeit}`, versatz)
    if (at === undefined) return
    if (new Date(at).getTime() > Date.now()) {
      setProblem(t.chat.wetterZukunft)
      return
    }
    onWahl({ lat: ort.lat, lon: ort.lon, at })
  }

  /** The photo's place and time into the fields -- and both stay open to change. */
  function ausFoto(bild: Reisefoto) {
    if (bild.lat === null || bild.lat === undefined || bild.lon === null || bild.lon === undefined) return
    const wann = bild.taken_at ?? null
    setFoto(bild)
    setOrt({ name: t.chat.wetterFotoOrt(wann === null ? '' : zeitText(wann, versatz)), lat: bild.lat, lon: bild.lon, gegend: '' })
    if (wann !== null) {
      const feld = zuFeld(wann, versatz)
      setTag(feld.slice(0, 10))
      setZeit(feld.slice(11, 16))
    }
    setProblem(null)
    setOffen(null)
  }

  if (offen === 'suche') {
    return createPortal(
      <Ortssuche
        tripId={tripId}
        onGewaehlt={(treffer) => {
          setOrt(treffer)
          setFoto(null)
          setProblem(null)
        }}
        onZu={() => setOffen(null)}
      />,
      document.body,
    )
  }
  if (offen === 'fotos') {
    return <Fotoortwahl tripId={tripId} versatz={versatz} onGewaehlt={ausFoto} onZu={() => setOffen(null)} />
  }

  return createPortal(
    <div className="rueckfrage" role="dialog" aria-modal="true" aria-label={t.chat.wetterWo} onClick={onAbbruch} data-wetterwahl="">
      <div className="rueckfrage__blatt" onClick={(event) => event.stopPropagation()}>
        <p className="rueckfrage__frage">{t.chat.wetterWo}</p>
        <div className="field field--dicht">
          <span className="field__label">{t.chat.wetterOrt}</span>
          {ort === null ? (
            <p className="leise leise--eng" data-wetterwahl-ort="">{t.chat.wetterKeinOrt}</p>
          ) : (
            <p className={foto === null ? 'wetterwahl__ort' : 'wetterwahl__ort wetterwahl__foto'} data-wetterwahl-ort={`${ort.lat},${ort.lon}`}>
              {foto !== null && <img src={foto.kind === 'video' ? (foto.poster_url ?? '') : (foto.thumb_url ?? foto.url)} alt="" />}
              <span>
                <span className="ortssuche__name">{ort.name}</span>
                {ort.gegend !== '' && <span className="ortssuche__gegend">{ort.gegend}</span>}
              </span>
            </p>
          )}
          <div className="wetterwahl__wege">
            <button type="button" className="button button--outline button--small" onClick={() => setOffen('suche')} data-wetterwahl-suchen="">
              {ort === null || foto !== null ? t.chat.wetterOrtSuchen : t.chat.wetterOrtAendern}
            </button>
            <button type="button" className="button button--outline button--small" onClick={() => setOffen('fotos')} data-wetterwahl-foto="">
              {t.chat.wetterAusFoto}
            </button>
          </div>
        </div>
        <div className="tagwahl__zeiten">
          <label className="field field--dicht">
            <span className="field__label">{t.chat.wetterTag}</span>
            <input className="field__input" type="date" value={tag} max={zuFeld(new Date().toISOString(), versatz).slice(0, 10)} onChange={(event) => { setTag(event.target.value); setProblem(null) }} data-wetterwahl-tag="" />
          </label>
          <label className="field field--dicht">
            <span className="field__label">{t.chat.wetterUhrzeit}</span>
            <input className="field__input" type="time" value={zeit} onChange={(event) => { setZeit(event.target.value); setProblem(null) }} data-wetterwahl-zeit="" />
          </label>
        </div>
        <p className="leise">{foto === null ? t.chat.wetterVorschlag : t.chat.wetterFotoHinweis}</p>
        {problem !== null && (
          <p className="notice" role="alert">{problem}</p>
        )}
        <div className="rueckfrage__knoepfe">
          <button type="button" className="button button--outline button--small" onClick={onAbbruch} autoFocus>
            {t.allgemein.abbrechen}
          </button>
          <button type="button" className="button button--small" onClick={senden} disabled={ort === null || tag === '' || zeit === ''} data-wetterwahl-senden="">
            {t.chat.wetterSenden}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

/**
 * The photos of this trip that carry coordinates -- one tap chooses. The
 * same tiles as `Fotowahl` on the map, but a single choice and no marks:
 * here a photo is a place and a time, not a picture to attach. Only the
 * traveller's own photos come from the server, like there.
 *
 * **Newest upload first, not newest shot.** From use: "sortieren nach
 * Upload-Zeit absteigend, d.h. das letzte Foto zuerst. Annahme: ein
 * Reisebericht wird geschrieben, und in dem Kontext wird auch das Wetter
 * veröffentlicht." The server lists by the time a photo was taken; the
 * upload order is the id, and that is what is sorted here.
 */
function Fotoortwahl({
  tripId,
  versatz,
  onGewaehlt,
  onZu,
}: {
  tripId: number
  versatz: Versatz
  onGewaehlt: (foto: Reisefoto) => void
  onZu: () => void
}) {
  const texte = useTexte()
  const t = texte.chat
  const fotos = useReisefotos(tripId, true)
  const mitOrt = (fotos.data?.media ?? [])
    .filter((m) => m.lat !== null && m.lat !== undefined && m.lon !== null && m.lon !== undefined)
    .sort((a, b) => b.id - a.id)

  return createPortal(
    <div className="ortssuche" role="dialog" aria-modal="true" aria-label={t.wetterFotoTitel} data-fotoortwahl="">
      <div className="ortssuche__blatt" onClick={(e) => e.stopPropagation()}>
        <div className="ortssuche__kopf">
          <h2 className="card__titel card__titel--dicht">{t.wetterFotoTitel}</h2>
          <button type="button" className="icon-button" onClick={onZu} aria-label={texte.allgemein.schliessen} title={texte.allgemein.schliessen}>
            <XIcon size={18} />
          </button>
        </div>
        {fotos.isPending ? (
          <p className="leise">{texte.karte.fotowahl.laedt}</p>
        ) : mitOrt.length === 0 ? (
          <p className="leise" data-fotoortwahl-leer="">{t.wetterFotoKeins}</p>
        ) : (
          <div className="fotowahl">
            {mitOrt.map((bild) => (
              <button type="button" key={bild.id} className="fotowahl__bild" onClick={() => onGewaehlt(bild)} data-fotoortwahl-bild={bild.id}>
                <img src={bild.kind === 'video' ? (bild.poster_url ?? '') : (bild.thumb_url ?? bild.url)} alt="" loading="lazy" />
                <span className={bild.taken_at ? 'fotowahl__wann' : 'fotowahl__wann fotowahl__wann--ohne'}>
                  {bild.taken_at ? zeitText(bild.taken_at, versatz) : texte.karte.fotowahl.ohneDatum}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}
